import { supabase } from "@/integrations/supabase/client";
import type { FlashcardDeck, Flashcard, FlashcardStudyStats } from "./types";
import { updateSM2, type ReviewRating, type SM2Fields } from "./sm2";

export async function fetchDecksForUser(
  userId: string,
  contentId?: string | null,
  chatId?: string | null
): Promise<FlashcardDeck[]> {
  let query = supabase
    .from("flashcard_decks")
    .select("*")
    .eq("user_id", userId)
    .eq("is_archived", false);

  if (contentId) {
    // ContentPage: Show only decks for this specific content
    query = query.eq("content_id", contentId);
  } else if (chatId) {
    // ChatPage: Show only decks for this specific chat
    query = query.eq("chat_id", chatId);
  } else {
    // No context: Show only orphan decks (no content, no chat)
    query = query.is("content_id", null).is("chat_id", null);
  }

  const { data, error } = await query.order("created_at", { ascending: false });

  if (error) throw error;
  return (data ?? []) as FlashcardDeck[];
}

export async function fetchCardsForDeck(deckId: string): Promise<Flashcard[]> {
  const { data, error } = await supabase
    .from("flashcards")
    .select("*")
    .eq("deck_id", deckId)
    .order("order_index", { ascending: true });

  if (error) throw error;
  return (data ?? []) as Flashcard[];
}

export async function getDueCards(deckId: string): Promise<Flashcard[]> {
  const now = new Date().toISOString();
  const { data, error } = await supabase
    .from("flashcards")
    .select("*")
    .eq("deck_id", deckId)
    .lte("due_at", now)
    .order("due_at", { ascending: true });

  if (error) throw error;
  return (data ?? []) as Flashcard[];
}

export async function reviewCard(cardId: string, rating: ReviewRating): Promise<void> {
  // First get current SM-2 values
  const { data, error: fetchError } = await supabase
    .from("flashcards")
    .select("ef, interval, repetitions, due_at")
    .eq("id", cardId)
    .single();

  if (fetchError) throw fetchError;

  const current: SM2Fields = {
    ef: data.ef ?? 2.5,
    interval: data.interval ?? 0,
    repetitions: data.repetitions ?? 0,
    due_at: data.due_at ?? new Date().toISOString(),
  };

  const updated = updateSM2(rating, current);

  const { error: updateError } = await supabase
    .from("flashcards")
    .update({
      ef: updated.ef,
      interval: updated.interval,
      repetitions: updated.repetitions,
      due_at: updated.due_at,
      updated_at: new Date().toISOString(),
    })
    .eq("id", cardId);

  if (updateError) throw updateError;

  // Record the review
  const { data: cardData } = await supabase
    .from("flashcards")
    .select("deck_id, user_id")
    .eq("id", cardId)
    .single();

  if (cardData) {
    await supabase.from("flashcard_reviews").insert({
      user_id: cardData.user_id,
      deck_id: cardData.deck_id,
      card_id: cardId,
      rating: rating === "again" ? 1 : rating === "hard" ? 3 : rating === "good" ? 4 : 5,
    });
  }
}

export async function toggleCardStar(cardId: string, isStarred: boolean) {
  const { error } = await supabase
    .from("flashcards")
    .update({ is_starred: !isStarred, updated_at: new Date().toISOString() })
    .eq("id", cardId);

  if (error) throw error;
}

export async function deleteCard(cardId: string) {
  const { error } = await supabase.from("flashcards").delete().eq("id", cardId);
  if (error) throw error;
}

export async function updateCardFields(
  cardId: string,
  fields: Partial<Pick<Flashcard, "term" | "definition" | "hint" | "explanation" | "source">>
) {
  const { error } = await supabase
    .from("flashcards")
    .update({ ...fields, updated_at: new Date().toISOString() })
    .eq("id", cardId);

  if (error) throw error;
}

export async function addCardToDeck(deckId: string, userId: string) {
  const { data: existing, error: fetchError } = await supabase
    .from("flashcards")
    .select("order_index")
    .eq("deck_id", deckId)
    .order("order_index", { ascending: false })
    .limit(1);

  if (fetchError) throw fetchError;

  const nextIndex = existing && existing.length > 0 ? existing[0].order_index + 1 : 0;

  const { error } = await supabase.from("flashcards").insert({
    deck_id: deckId,
    user_id: userId,
    term: "",
    definition: "",
    hint: "",
    explanation: "",
    source: "manual",
    order_index: nextIndex,
    ef: 2.5,
    interval: 0,
    repetitions: 0,
    due_at: new Date().toISOString(),
  });

  if (error) throw error;
}

export async function deleteDeck(deckId: string) {
  const { error } = await supabase.from("flashcard_decks").delete().eq("id", deckId);
  if (error) throw error;
}

export async function updateDeckTitle(deckId: string, title: string) {
  const { error } = await supabase
    .from("flashcard_decks")
    .update({ title, updated_at: new Date().toISOString() })
    .eq("id", deckId);
  if (error) throw error;
}

export async function generateFlashcardsWithAI(params: {
  userId: string;
  contentId?: string | null;
  chatId?: string | null;
  topic?: string | null;
  sourceText?: string | null;
  numCards?: number;
  contentType?: string | null;
}) {
  const { userId, contentId, chatId, topic, sourceText, numCards, contentType } = params;

  const { data, error } = await supabase.functions.invoke("generate-flashcards", {
    body: {
      user_id: userId,
      content_id: contentId ?? null,
      chat_id: chatId ?? null,
      topic: topic ?? null,
      source_text: sourceText ?? null,
      num_cards: numCards ?? 10,
    },
  });

  if (error) throw error;
  const result = data as { deck: FlashcardDeck; cards: Flashcard[] };
  
  const { trackEvent } = await import("@/lib/analytics");
  trackEvent("flashcards_generated", {
    content_id: contentId ?? null,
    content_type: contentType ?? null,
    card_count: result.cards.length,
  });
  
  return result;
}

export function computeStudyStats(cards: Flashcard[]): FlashcardStudyStats {
  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  
  let notStudied = 0;
  let toReview = 0;
  let studiedToday = 0;

  for (const card of cards) {
    const dueDate = new Date(card.due_at);
    const isNeverTouched = card.repetitions === 0 && card.updated_at === card.created_at;
    
    if (isNeverTouched) {
      // Truly never studied (never reviewed at all)
      notStudied++;
    } else if (dueDate <= now) {
      // Due for review (includes cards rated "Again" that are due again)
      toReview++;
    }

    // Check if studied today (was updated today and has been touched)
    const updatedAt = new Date(card.updated_at);
    if (!isNeverTouched && updatedAt >= todayStart) {
      studiedToday++;
    }
  }

  return {
    notStudied,
    toReview,
    studiedToday,
  };
}
